import { Tooltip } from "~/components/reusable/Tooltip"
import { INVENTORY_CALENDAR_PRODUCT_TABLE_ID } from "~/constants"
import { useCompany } from "~/hooks/useCompany"
import { useFilters } from "~/hooks/useFilters"
import { useTableSearch } from "~/hooks/useTableSearch"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { useSkuStore } from "~/providers/sku-store-provider"
import { api } from "~/utils/api"
import { startOfTodayUTC } from "~/utils/datetime"
import { filterVariants } from "~/utils/filter"
import { formatNumber } from "~/utils/formatting"
import { getTailwindValue } from "~/utils/tailwind"
import { uniq } from "~/utils/tsUtils"
import { Badge } from "~/components/reusable/Badge"
import Select from "~/components/reusable/Select"
import { type PresetView } from "~/components/planning/presetView"
import ProductTableV2 from "~/components/planning/ProductTable/ProductTableV2"
import ViewConfiguration from "~/components/planning/ViewConfiguration"
import { AnalysisPage } from "~/components/design-system/pages"
import { InventoryGraphCard } from "~/components/home/InventoryGraphCard"
import { CalendarBlank, Info } from "@phosphor-icons/react"
import { type ColDef } from "ag-grid-community"
import { isBefore } from "date-fns"
import { startCase } from "lodash"
import { createSumColumn } from "~/utils/columnTemplates"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSessionStorage } from "usehooks-ts"
import { YearSelector } from "~/components/reusable/YearSelector"
import type { DynamicColumnId } from "~/components/planning/ProductTable/utils/columnUtils"
import {
  type CalendarViewOption,
  calendarColumnEnd,
  calendarYearRange,
  formatCalendarHeader,
  formatCalendarSubHeader,
  generateCalendarDatesForYear,
  isSameCalendarPeriod,
} from "~/utils/calendarUtils"
import { useVariants } from "~/hooks/useVariants"
import { skuIdentityColumnDefs } from "~/components/planning/ProductTable/utils/skuDetailColumns"
import CustomAgGridTooltip from "~/components/reusable/table/tooltips/CustomTooltip"
import { collapseMasterRows } from "~/utils/ag-utils"
import {
  INVENTORY_COST_TOOLTIP_TYPE,
  type InventoryCostTooltipValue,
  skuInventoryCost,
  variantInventoryCost,
} from "~/utils/inventoryCalendar"

enum InventoryCalendarViewOptions {
  Weekly = "weekly",
  Monthly = "monthly",
  Quarterly = "quarterly",
}

enum InventoryCalendarMetricOption {
  Units = "Units",
  Cost = "Cost",
}

const INVENTORY_CALENDAR_METRIC_STORAGE_KEY = "inventory-calendar-metric"

const viewOptions = Object.values(InventoryCalendarViewOptions).map((o) => ({
  label: startCase(o),
  value: o,
}))

const getMetricOptions = (currency: string) =>
  Object.values(InventoryCalendarMetricOption).map((o) => ({
    label: o === InventoryCalendarMetricOption.Units ? o : `${o} (${currency})`,
    value: o,
  }))

type Props = {
  presetViews?: PresetView[]
  presetGroupId?: string
}

/** A row of the variant's SKU detail grid. `unitsByPeriod` is keyed by period-start ISO; null = no data. */
type InventorySkuDetailRow = {
  skuId: string
  size: string
  variantId: string
  costPrice: number | null
  unitsByPeriod: Record<string, number | null>
}

const isSkuDetailRow = (data: unknown): data is InventorySkuDetailRow =>
  typeof data === "object" && data !== null && "unitsByPeriod" in data

const SKU_WITHOUT_COST_PRICE =
  "This SKU has no cost price, so its inventory value is not calculated."

const SkuDetailNoRowsOverlay = ({ failed }: { failed: () => boolean }) => (
  <span className="text-fg-muted paragraph-sm">
    {failed()
      ? "SKU stock could not be loaded. Collapse and expand the row to try again."
      : "No SKUs to show for this variant."}
  </span>
)

export default function InventoryCalendar({ presetViews, presetGroupId }: Props = {}) {
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const selectedChannel = useSkuStore((state) => state.channel)
  const rawVariants = useVariants()
  const { filters } = useFilters()
  const { searchText } = useTableSearch(INVENTORY_CALENDAR_PRODUCT_TABLE_ID)

  const { data: company } = useCompany()
  const gridRef = useRef<any>(null)
  const [isDataRendered, setIsDataRendered] = useState(false)

  const metricOptions = getMetricOptions(company?.currency ?? "")

  const variants = useMemo(() => {
    return filterVariants(filters, rawVariants)
  }, [rawVariants, filters])

  const inspectorVariants = useMemo(() => {
    return filterVariants(filters, rawVariants, searchText)
  }, [rawVariants, filters, searchText])

  const variantIds = useMemo(() => {
    return variants.map((v) => v.variantId)
  }, [variants])

  const inspectorVariantIds = useMemo(
    () => inspectorVariants.map((v) => v.variantId),
    [inspectorVariants],
  )

  const [viewOption, setViewOption] = useSessionStorage(
    "inventory-calendar-view",
    InventoryCalendarViewOptions.Monthly,
  )
  const [metricOption, setMetricOption] = useSessionStorage(
    INVENTORY_CALENDAR_METRIC_STORAGE_KEY,
    InventoryCalendarMetricOption.Units,
  )

  const currentYear = new Date().getFullYear()
  // StockRecord table only has 1 year of historical data
  const minYear = currentYear - 1
  const maxYear = currentYear + 1
  const [selectedYear, setSelectedYear] = useSessionStorage<number>(
    "inventory-calendar-year",
    currentYear,
  )

  const calendarDates = useMemo(
    () => generateCalendarDatesForYear(selectedYear, viewOption as CalendarViewOption),
    [selectedYear, viewOption],
  )

  const isCurrentOrFutureYear = selectedYear >= currentYear
  // Stable per day so the memos below don't rebuild every render.
  const todayMs = startOfTodayUTC().getTime()
  const today = useMemo(() => new Date(todayMs), [todayMs])

  // Align the data window with the displayed columns: a fiscal weekly view spans the fiscal year.
  const { start: historicStartDate, end: historicEndDate } = useMemo(
    () => calendarYearRange(selectedYear, viewOption as CalendarViewOption),
    [selectedYear, viewOption],
  )

  const {
    data: closingStockOutput,
    isLoading: loadingClosingStock,
    isPending: pendingClosingStock,
    isFetching: fetchingClosingStock,
  } = api.product.historicInventoryLevels.useQuery({
    interval: viewOption as "weekly" | "monthly" | "quarterly",
    warehouseId: selectedWarehouseId,
    startDate: historicStartDate,
    endDate: historicEndDate,
  })

  const { data: historicClosingStocks, dates: closingStockDates } = closingStockOutput ?? {}

  const futureClosingStockDates = useMemo(() => {
    if (!isCurrentOrFutureYear) return []
    return (
      calendarDates
        .map((calendarDate) => {
          // calendarDate = start of the period we want to display
          // For closing stock, we want the stock level AT the start of this period
          // endDate is the end of this period (start of next). Weekly keeps its original
          // zero-width window (current week excluded); monthly/quarterly use the period end
          // (fiscal-aware) so the current period is included as before.
          const endDate =
            viewOption === InventoryCalendarViewOptions.Weekly
              ? calendarDate
              : calendarColumnEnd(calendarDate, viewOption as CalendarViewOption)
          return { calendarDate, endDate, startDate: calendarDate }
        })
        // Include periods where the END date is >= today (current period or future)
        .filter((fd) => !isBefore(fd.endDate, today))
    )
  }, [calendarDates, isCurrentOrFutureYear, viewOption, today])

  // Create a map from calendarDate timestamp to index in futureClosingStockDates
  const futureIndexMap = useMemo(() => {
    const map = new Map<number, number>()
    futureClosingStockDates.forEach((fd, index) => {
      map.set(fd.calendarDate.getTime(), index)
    })
    return map
  }, [futureClosingStockDates])

  const {
    data: forecastedClosingStockData,
    isLoading: loadingForecastedClosingStock,
    isFetching: fetchingForecastedClosingStock,
    isPending: pendingForecastedClosingStock,
  } = api.closingStock.getForecastedClosingStockForDays.useQuery(
    {
      variantIds,
      warehouseId: selectedWarehouseId,
      dates: futureClosingStockDates.map((x) => x.startDate),
    },
    {
      enabled: futureClosingStockDates.length > 0,
      select: (data) => data as Record<string, number[]>,
    },
  )

  // Only include forecasted stock loading states when we need that data (current/future years)
  const needsForecastedStock = futureClosingStockDates.length > 0
  const isLoading = loadingClosingStock || (needsForecastedStock && loadingForecastedClosingStock)
  const isPending = pendingClosingStock || (needsForecastedStock && pendingForecastedClosingStock)
  const isFetching =
    fetchingClosingStock || (needsForecastedStock && fetchingForecastedClosingStock)

  const isCurrentPeriod = useCallback(
    (date: Date) => {
      const now = new Date()
      return isSameCalendarPeriod(date, now, viewOption as CalendarViewOption)
    },
    [viewOption],
  )

  const handleFirstDataRendered = useCallback(() => {
    setIsDataRendered(true)
  }, [])

  useEffect(() => {
    if (!isDataRendered || !gridRef.current?.api) return
    const currentPeriodDate = calendarDates.find((d) => isCurrentPeriod(d))
    if (currentPeriodDate) {
      const index = calendarDates.indexOf(currentPeriodDate)
      let prefix = ""
      switch (viewOption) {
        case InventoryCalendarViewOptions.Weekly:
          prefix = "Week"
          break
        case InventoryCalendarViewOptions.Monthly:
          prefix = "Month"
          break
        case InventoryCalendarViewOptions.Quarterly:
          prefix = "Quarter"
          break
      }
      gridRef.current.api.ensureColumnVisible(`calendar-inventory-${prefix}-${index}`, "middle")
    }
  }, [isDataRendered, calendarDates, isCurrentPeriod, viewOption])

  const matchesPeriod = useCallback(
    (checkDate: Date, targetDate: Date) =>
      isSameCalendarPeriod(checkDate, targetDate, viewOption as CalendarViewOption),
    [viewOption],
  )

  const periods = useMemo(
    () =>
      calendarDates.map((date) => ({
        date,
        key: date.toISOString(),
        // A period is "past" only if it's before today AND not the current period
        // Current period should show projected stock, not historic
        isPast: isBefore(date, today) && !isCurrentPeriod(date),
        // Direct lookup using timestamp - much more reliable than date comparison
        futureIndex: futureIndexMap.get(date.getTime()) ?? -1,
      })),
    [calendarDates, today, isCurrentPeriod, futureIndexMap],
  )

  const extraColumns: ColDef<IVariantSummary>[] = useMemo(() => {
    let prefix = ""

    switch (viewOption) {
      case InventoryCalendarViewOptions.Weekly:
        prefix = "Week"
        break
      case InventoryCalendarViewOptions.Monthly:
        prefix = "Month"
        break
      case InventoryCalendarViewOptions.Quarterly:
        prefix = "Quarter"
        break
    }

    const calendarColumns: ColDef<IVariantSummary>[] = periods.map((period, index) => {
      const { date: currentDate, key: periodKey, isPast, futureIndex } = period
      const headerName = formatCalendarHeader(currentDate, viewOption as CalendarViewOption)

      const historicIndex = closingStockDates?.findIndex((d) => matchesPeriod(d, currentDate))

      const getUnits = (data: unknown): number | "N/A" | null => {
        if (isSkuDetailRow(data)) return data.unitsByPeriod[periodKey] ?? "N/A"
        const variantId = (data as IVariantSummary | undefined)?.variantId
        if (!variantId) return null
        if (isPast) {
          if (historicIndex === undefined || historicIndex === -1) return "N/A"
          return historicClosingStocks?.get(variantId)?.[historicIndex] ?? "N/A"
        }
        if (futureIndex === -1) return "N/A"
        return forecastedClosingStockData?.[variantId]?.[futureIndex] ?? "N/A"
      }

      return createSumColumn<IVariantSummary>({
        colId: `calendar-inventory-${prefix}-${index}` as DynamicColumnId,
        headerName,
        headerSubtitle: formatCalendarSubHeader(currentDate, viewOption as CalendarViewOption),
        minWidth: 105,
        // Sizes the SKU detail grid; the parent grid flexes.
        initialWidth: 110,
        suppressMovable: true,
        lockVisible: true,
        // Past periods get a background color; future periods use default styling from template
        ...(isPast && {
          cellStyle: {
            backgroundColor: getTailwindValue("--background-color-medium"),
          },
        }),
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined) return ""
          if (params.value === "N/A") return "N/A"
          return formatNumber(params.value, 0)
        },
        cellRenderer: (params: any) => {
          if (params.value === "N/A") {
            return <span className="text-fg-muted">N/A</span>
          }
          return params.valueFormatted ?? params.value
        },
        tooltip: (params): string | InventoryCostTooltipValue | undefined => {
          const data: unknown = params.data
          const units = getUnits(data)
          if (params.value === "N/A") {
            if (typeof units === "number") return SKU_WITHOUT_COST_PRICE
            return isPast
              ? "No closing stock data available for this period"
              : "Projected stock data not available for this period"
          }
          if (metricOption !== InventoryCalendarMetricOption.Cost) return undefined
          if (typeof params.value !== "number") return undefined
          if (params.node?.group) {
            return {
              tooltipType: INVENTORY_COST_TOOLTIP_TYPE,
              level: "group",
              value: params.value,
            }
          }
          if (typeof units !== "number") return undefined
          if (isSkuDetailRow(data)) {
            if (data.costPrice == null) return undefined
            return {
              tooltipType: INVENTORY_COST_TOOLTIP_TYPE,
              level: "sku",
              units,
              costPrice: data.costPrice,
              value: params.value,
            }
          }
          return {
            tooltipType: INVENTORY_COST_TOOLTIP_TYPE,
            level: "variant",
            units,
            costPrice: (data as IVariantSummary | undefined)?.costPrice ?? null,
            value: params.value,
          }
        },
        valueGetter: (params) => {
          const data: unknown = params.data
          const units = getUnits(data)
          if (typeof units !== "number") return units
          switch (metricOption) {
            case InventoryCalendarMetricOption.Units:
              return units
            case InventoryCalendarMetricOption.Cost:
              return isSkuDetailRow(data)
                ? skuInventoryCost(units, data.costPrice)
                : variantInventoryCost(units, (data as IVariantSummary | undefined)?.costPrice)
          }
        },
      })
    })

    return calendarColumns
  }, [
    viewOption,
    periods,
    matchesPeriod,
    closingStockDates,
    historicClosingStocks,
    forecastedClosingStockData,
    metricOption,
  ])

  const trpcUtils = api.useUtils()
  const failedDetailVariantIds = useRef(new Set<string>())

  const overrideDetailCellRendererParams = useCallback(
    (masterParams: { data?: IVariantSummary }) => {
      const masterVariantId = masterParams.data?.variantId ?? ""
      const futureDates = futureClosingStockDates.map((x) => x.startDate)
      const hasPastPeriods = periods.some((p) => p.isPast)

      return {
        detailGridOptions: {
          defaultColDef: {
            resizable: true,
            sortable: true,
            suppressHeaderMenuButton: true,
            tooltipComponent: CustomAgGridTooltip,
          },
          // Anchor popups to body so async tooltip positioning can't dereference
          // a null grid div if the detail grid unmounts mid-show.
          popupParent: document.body,
          tooltipInteraction: true,
          tooltipShowDelay: 750,
          noRowsOverlayComponent: SkuDetailNoRowsOverlay,
          noRowsOverlayComponentParams: {
            failed: () => failedDetailVariantIds.current.has(masterVariantId),
          },
          columnDefs: [...skuIdentityColumnDefs(), ...extraColumns],
        },
        getDetailRowData: async (params: {
          data: IVariantSummary
          successCallback: (rows: InventorySkuDetailRow[]) => void
        }) => {
          const variant = params.data
          const skuIds = variant.skuIds ?? []
          failedDetailVariantIds.current.delete(variant.variantId)
          if (skuIds.length === 0) {
            params.successCallback([])
            return
          }

          try {
            const [historic, projected, skuInfo] = await Promise.all([
              hasPastPeriods
                ? trpcUtils.product.historicInventoryLevelsBySku.fetch({
                    interval: viewOption as "weekly" | "monthly" | "quarterly",
                    warehouseId: selectedWarehouseId,
                    startDate: historicStartDate,
                    endDate: historicEndDate,
                    variantId: variant.variantId,
                  })
                : undefined,
              // Same dates as the variant query, so SKU rows sum to the variant row.
              futureDates.length > 0
                ? (trpcUtils.closingStock.getForecastedClosingStockForDays.fetch({
                    variantIds: [variant.variantId],
                    warehouseId: selectedWarehouseId,
                    dates: futureDates,
                    aggregationKey: "skuId",
                  }) as Promise<Record<string, number[]>>)
                : undefined,
              trpcUtils.sku.getSkuInfo.fetch({ variantIds: [variant.variantId] }),
            ])

            // `variant.skuIds` holds active SKUs only, but the stock queries count every shown SKU,
            // so an inactive SKU still holding stock needs its own row for the sum to reconcile.
            const skuIdSet = new Set(skuIds)
            const extraSkuIds = uniq([
              ...(historic?.data.keys() ?? []),
              ...Object.keys(projected ?? {}),
            ]).filter((skuId) => !skuIdSet.has(skuId))

            const historicIndexByPeriod = new Map(
              periods.map((p) => [
                p.key,
                historic?.dates.findIndex((d) => matchesPeriod(d, p.date)) ?? -1,
              ]),
            )

            const rows = [...skuIds, ...extraSkuIds].map((skuId): InventorySkuDetailRow => {
              const unitsByPeriod: Record<string, number | null> = {}
              for (const period of periods) {
                if (period.isPast) {
                  const index = historicIndexByPeriod.get(period.key) ?? -1
                  unitsByPeriod[period.key] =
                    index === -1 ? null : (historic?.data.get(skuId)?.[index] ?? null)
                } else {
                  unitsByPeriod[period.key] =
                    period.futureIndex === -1
                      ? null
                      : (projected?.[skuId]?.[period.futureIndex] ?? null)
                }
              }
              const sku = skuInfo.get(skuId)
              return {
                skuId,
                size: sku?.size ?? "",
                variantId: variant.variantId,
                costPrice: sku?.costPrice ?? null,
                unitsByPeriod,
              }
            })
            params.successCallback(rows)
          } catch (e) {
            console.error("Error fetching SKU inventory calendar data:", e)
            failedDetailVariantIds.current.add(variant.variantId)
            params.successCallback([])
          }
        },
      }
    },
    [
      extraColumns,
      periods,
      futureClosingStockDates,
      matchesPeriod,
      viewOption,
      selectedWarehouseId,
      historicStartDate,
      historicEndDate,
      trpcUtils,
    ],
  )

  // Detail grids keep the columns and data they opened with, so collapse before either changes;
  // collapsing in an effect afterwards leaves a stale detail row. ProductTableV2 handles warehouse.
  const collapseAllMasterRows = () => collapseMasterRows(gridRef.current?.api)

  const handleSetMetricOption = (option: InventoryCalendarMetricOption) => {
    collapseAllMasterRows()
    setMetricOption(option)
  }

  const handleSetViewOption = (option: InventoryCalendarViewOptions) => {
    collapseAllMasterRows()
    setViewOption(option)
  }

  const handleSetSelectedYear = (year: number) => {
    collapseAllMasterRows()
    setSelectedYear(year)
  }

  const customGridOptions = useMemo(
    () => ({
      onFirstDataRendered: handleFirstDataRendered,
    }),
    [handleFirstDataRendered],
  )

  const closingStockBadge = (
    <Badge className="bg-badge-blue-bg border-badge-blue-bg">
      <Tooltip
        body={
          <div className="flex flex-col gap-4 text-left">
            <p className="font-bold">
              This calendar shows the closing stock: the stock level at the end of each period.
            </p>
            <ul className="flex flex-col gap-2 list-disc pl-4">
              <li>
                You can view closing stock in either <span className="font-semibold">units</span> or{" "}
                <span className="font-semibold">inventory value</span>.
              </li>
              <li>
                The calendar can be displayed by <span className="font-semibold">week</span>,{" "}
                <span className="font-semibold">month</span>, or{" "}
                <span className="font-semibold">quarter</span>.
              </li>
              <li>
                <span className="font-semibold">Predicted future closing stock</span> takes into
                account the current stock level, forecasted demand, and incoming stock.
              </li>
            </ul>
          </div>
        }
        side="bottom"
      >
        <div className="flex flex-row gap-1 items-center">
          Closing Stock <Info size={12} />
        </div>
      </Tooltip>
    </Badge>
  )

  const extraDisplayOptions = (
    <>
      <div className="flex flex-row items-center justify-between gap-2">
        <span className="label-base">Metric</span>
        <Select
          options={metricOptions}
          value={metricOptions.find((o) => o.value === metricOption)}
          onChange={(option) =>
            handleSetMetricOption(option?.value ?? InventoryCalendarMetricOption.Units)
          }
          size="sm"
        />
      </div>
      {metricOption === InventoryCalendarMetricOption.Cost && (
        <p className="paragraph-xs text-fg-muted max-w-[50ch]">
          Variant rows use the average SKU cost price. SKU rows use each SKU&apos;s own cost price.
        </p>
      )}
      <div className="flex flex-row items-center justify-between gap-2">
        <span className="label-base">Period</span>
        <Select
          options={viewOptions}
          value={viewOptions.find((o) => o.value === viewOption)}
          onChange={(option) =>
            handleSetViewOption(option?.value ?? InventoryCalendarViewOptions.Weekly)
          }
          size="sm"
          leftIcon={<CalendarBlank />}
        />
      </div>
    </>
  )

  const isMonthlyOrQuarterly =
    viewOption === InventoryCalendarViewOptions.Monthly ||
    viewOption === InventoryCalendarViewOptions.Quarterly

  return (
    <AnalysisPage
      fillContent
      inspectorWidth="lg"
      inspectorDefaultOpen={false}
      viewConfiguration={
        <ViewConfiguration
          tableId={INVENTORY_CALENDAR_PRODUCT_TABLE_ID}
          presetViews={presetViews}
          presetGroupId={presetGroupId}
          primaryControl={
            <>
              {closingStockBadge}
              <YearSelector
                selectedYear={selectedYear}
                onChange={handleSetSelectedYear}
                minYear={minYear}
                maxYear={maxYear}
              />
            </>
          }
          extraDisplayOptions={extraDisplayOptions}
        />
      }
      inspector={
        <InventoryGraphCard
          variantIds={inspectorVariantIds}
          channel={selectedChannel}
          warehouseId={selectedWarehouseId}
          isMonthly={isMonthlyOrQuarterly}
          defaultShownSeries={[
            "Closing stock",
            "Projected stock",
            "Safety stock",
            "Incoming stock",
          ]}
          metric={metricOption}
        />
      }
    >
      <ProductTableV2
        loading={isLoading || isPending || isFetching}
        gridRef={gridRef}
        id={INVENTORY_CALENDAR_PRODUCT_TABLE_ID}
        variants={variants}
        warehouseId={selectedWarehouseId}
        channel={selectedChannel}
        extraColumns={extraColumns}
        enableSearch={true}
        customGridOptions={customGridOptions}
        masterDetail
        // A kept detail grid would reopen with the warehouse/metric/period it was built for.
        keepDetailRows={false}
        overrideDetailCellRendererParams={overrideDetailCellRendererParams}
      />
    </AnalysisPage>
  )
}
